import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, Phone, MessageSquareText, ClipboardList } from "lucide-react";
import { useSite } from "../../lib/site";
import { track } from "../../lib/api";
import { CallButton } from "./Blocks";
import { AREA_LINE, CITIES } from "../../lib/cities";

const NAV = [
  ["/services", "Services"],
  ["/senior-home-support", "Senior Support"],
  ["/before-and-after", "Before & After"],
  ["/service-areas", "Service Areas"],
  ["/about", "About"],
  ["/contact", "Contact"],
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-[#131413] text-white">
      <div className="bg-[#D9232E] text-center text-sm font-bold">
        <Link to="/senior-home-support" className="block px-4 py-2 hover:underline" data-testid="announcement-senior-link">Seniors save 20% for life on eligible services — see how it works</Link>
      </div>
      <div className="wrap flex max-w-7xl items-center justify-between gap-4 py-3">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="One Punch Services home">
          <img src="/brand/mark.png" alt="" className="h-12 w-auto" />
          <span className="font-display text-2xl font-extrabold uppercase leading-none">One Punch<span className="block text-sm tracking-[0.3em] text-[#ff4651]">Services</span></span>
        </Link>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {NAV.map(([to, label]) => (
            <NavLink key={to} to={to} className={({ isActive }) => `whitespace-nowrap rounded px-2.5 py-2 text-base font-medium hover:bg-white/10 ${isActive ? "text-[#ff4651]" : ""}`} data-testid={`nav-${to.slice(1)}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-2 lg:flex">
          <CallButton className="btn-sm btn whitespace-nowrap border-2 border-white/60 text-white hover:bg-white/10" label="505-596-0325" testid="header-call-btn" />
          <Link to="/quote" className="btn-red btn-sm whitespace-nowrap" data-testid="header-quote-btn" onClick={() => track("quote_cta_click")}>Get a Quote</Link>
        </div>
        <button className="inline-flex h-12 w-12 items-center justify-center rounded border border-white/40 lg:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Menu" data-testid="mobile-menu-btn">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <nav className="border-t border-white/10 lg:hidden" aria-label="Mobile">
          <div className="wrap flex flex-col py-2">
            {[["/", "Home"], ...NAV, ["/quote", "Request a Quote"]].map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="border-b border-white/10 py-4 text-lg font-medium" data-testid={`mobile-nav-${to.slice(1) || "home"}`}>{label}</Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { site, phone, tel } = useSite();
  const b = site.business || {};
  return (
    <footer className="bg-[#131413] pb-28 text-neutral-300 md:pb-0">
      <div className="wrap grid gap-10 py-14 md:grid-cols-3">
        <div>
          <img src="/brand/logo.png" alt="One Punch Services logo" className="w-48" />
          <p className="mt-4 text-lg font-bold text-white">Junk Gone. Repairs Done.</p>
          <p className="text-base">We Knock It Out for You.</p>
        </div>
        <div className="space-y-2 text-base">
          <p className="font-bold text-white">Contact</p>
          <p><a href={tel} className="underline-offset-4 hover:underline" onClick={() => track("call_click")} data-testid="footer-phone-link">Call or text {phone}</a></p>
          {b.email && <p><a href={`mailto:${b.email}`} className="break-all hover:underline" data-testid="footer-email-link">{b.email}</a></p>}
          <p data-testid="footer-area-line">{AREA_LINE}</p>
          <p className="flex flex-wrap gap-x-3 text-sm">{CITIES.map((c) => <Link key={c.slug} to={`/service-areas/${c.slug}`} className="underline hover:text-white" data-testid={`footer-city-${c.slug}`}>{c.name}</Link>)}</p>
          {b.hours && <p>Hours: {b.hours}</p>}
          {b.credential_note && <p className="text-sm text-neutral-400">{b.credential_note}</p>}
        </div>
        <div className="grid grid-cols-2 gap-2 text-base">
          {[["/services", "Services"], ["/senior-home-support", "Senior Support"], ["/quote", "Request a Quote"], ["/before-and-after", "Before & After"], ["/service-areas", "Service Areas"], ["/about", "About"], ["/contact", "Contact"], ["/privacy", "Privacy Policy"], ["/terms", "Service & Discount Terms"], ["/admin", "Owner login"]].map(([to, l]) => (
            <Link key={to} to={to} className="py-1 hover:text-white hover:underline" data-testid={`footer-link-${to.slice(1)}`}>{l}</Link>
          ))}
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-sm">© {new Date().getFullYear()} One Punch Services. Locally owned and based in Albuquerque, NM.</div>
    </footer>
  );
}

function MobileBar() {
  const { tel, sms } = useSite();
  const cls = "flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5 text-sm font-bold min-h-[60px]";
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex border-t border-black bg-[#171717] text-white md:hidden" data-testid="mobile-sticky-bar">
      <a href={tel} className={cls} onClick={() => track("call_click")} data-testid="sticky-call-btn"><Phone className="h-5 w-5" aria-hidden="true" />Call</a>
      <a href={sms} className={`${cls} border-x border-white/15`} onClick={() => track("sms_click")} data-testid="sticky-text-btn"><MessageSquareText className="h-5 w-5" aria-hidden="true" />Text Photos</a>
      <Link to="/quote" className={`${cls} bg-[#D9232E]`} onClick={() => track("quote_cta_click")} data-testid="sticky-quote-btn"><ClipboardList className="h-5 w-5" aria-hidden="true" />Get a Quote</Link>
    </div>
  );
}

export default function Layout() {
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[60] focus:bg-white focus:p-3 focus:text-black">Skip to content</a>
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
}
